import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import { PublicWorkerPool } from "types/generated";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "views/Account/WorkerPool/QueuedRunsView/constants";

import { SEARCH_PUBLIC_WORKER_POOL_SCHEDULABLE_RUNS } from "./gql";

const useSearchPublicQueuedRuns = () => {
  const { onError } = useTypedContext(FlashContext);

  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(searchParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [searchParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(searchParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [searchParams]);

  const { error, loading, data, refetch, previousData } = usePolledQuery<{
    publicWorkerPool: PublicWorkerPool;
  }>(SEARCH_PUBLIC_WORKER_POOL_SCHEDULABLE_RUNS, {
    variables: {
      input: {
        predicates,
        fullTextSearch: searchInput,
        // "first" & "after" arguments are ignored by the server, no pagination is needed here
        first: null,
        after: null,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    // APOLLO CLIENT UPDATE
  });

  const rawSearchRuns = useMemo(() => {
    const dataWorkerPool = data?.publicWorkerPool || previousData?.publicWorkerPool;
    if (dataWorkerPool) {
      const { searchSchedulableRuns } = dataWorkerPool;

      return searchSchedulableRuns;
    }

    return undefined;
  }, [data?.publicWorkerPool, previousData?.publicWorkerPool]);

  const memoizedEntities = useMemo(() => {
    return rawSearchRuns?.edges.map((edge) => edge.node) || [];
  }, [rawSearchRuns?.edges]);

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (error) {
      onError(error);
    }
  };

  return {
    entities: memoizedEntities,
    error,
    loading,
    isPageEmpty: !!(data && !memoizedEntities.length && !searchInput && predicates.length === 0),
    hasNoFilteringResults:
      !!data && !memoizedEntities.length && (!!searchInput || predicates.length > 0),
    refetch: handleRefetch,
  };
};

export default useSearchPublicQueuedRuns;
